import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Sheet z briefingiem oferty + ROI stats. Otwierany przyciskiem przy wierszu w ProductsPage.
// Po stronie backendu: GET/PUT /api/offers/{id}/extras, GET /api/offers/{id}/stats

const SEGMENTS = ['hot', 'warm', 'cold', 'dead'];
const CHANNELS = ['sms', 'email'];
const CONV_EVENTS = ['click', 'form_submit', 'phone_call', 'custom'];
const DEFAULT_EXTRAS = {
  target_segments: ['warm', 'hot'],
  target_demographics: {},
  channels: ['sms', 'email'],
  validity_from: null,
  validity_to: null,
  agent_briefing: '',
  copy_examples: [],
  supporting_articles: [],
  conversion_event: 'click',
  payout_per_conversion: 0.0,
  daily_send_cap: 0,
  per_contact_cooldown_days: 7,
  epidemos_category: null,
  notes: '',
};

export default function OfferAgentBriefing({ offer, open, onOpenChange }) {
  const [extras, setExtras] = useState(DEFAULT_EXTRAS);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newArticle, setNewArticle] = useState('');
  const [newCopy, setNewCopy] = useState({ channel: 'sms', subject: '', body: '', note: '' });

  useEffect(() => {
    if (!offer?.id || !open) return;
    api.get(`/offers/${offer.id}`)
      .then(r => setExtras({ ...DEFAULT_EXTRAS, ...(r.data.extras || {}) }))
      .catch(() => setExtras(DEFAULT_EXTRAS));
    api.get(`/offers/${offer.id}/stats`).then(r => setStats(r.data)).catch(() => setStats(null));
  }, [offer?.id, open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/offers/${offer.id}/extras`, extras);
      toast.success('Briefing zapisany');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Błąd zapisu');
    } finally { setSaving(false); }
  };

  const toggleArr = (key, val) => {
    setExtras(prev => {
      const cur = prev[key] || [];
      return { ...prev, [key]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
    });
  };

  const addArticle = () => {
    if (!newArticle.trim()) return;
    setExtras(prev => ({ ...prev, supporting_articles: [...(prev.supporting_articles || []), newArticle.trim()] }));
    setNewArticle('');
  };
  const removeArticle = (url) => {
    setExtras(prev => ({ ...prev, supporting_articles: prev.supporting_articles.filter(u => u !== url) }));
  };
  const addCopy = () => {
    if (!newCopy.body.trim()) return;
    setExtras(prev => ({ ...prev, copy_examples: [...(prev.copy_examples || []), { ...newCopy }] }));
    setNewCopy({ channel: 'sms', subject: '', body: '', note: '' });
  };
  const removeCopy = (idx) => {
    setExtras(prev => ({ ...prev, copy_examples: prev.copy_examples.filter((_, i) => i !== idx) }));
  };

  if (!offer) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading">Briefing agenta · {offer.name}</SheetTitle>
          <SheetDescription>
            Te dane czyta autonomiczny agent (Sprint 4) — żeby wiedzieć dla kogo, jakim tonem, z jakimi przykładami i artykułami wsparcia.
          </SheetDescription>
        </SheetHeader>

        {/* ROI Stats */}
        {stats && (
          <div className="mt-4 p-3 rounded-lg bg-muted/30 border border-border/30">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">ROI ({stats.period_days || 30} dni)</p>
            <div className="grid grid-cols-4 gap-2 text-sm">
              <div><div className="text-xl font-mono">{stats.sent_total}</div><div className="text-[10px] opacity-60">wysłane</div></div>
              <div><div className="text-xl font-mono">{stats.clicks}</div><div className="text-[10px] opacity-60">kliknięć</div></div>
              <div><div className="text-xl font-mono">{stats.conversions}</div><div className="text-[10px] opacity-60">konwersji</div></div>
              <div><div className="text-xl font-mono">{stats.revenue_total} zł</div><div className="text-[10px] opacity-60">przychód</div></div>
            </div>
            <div className="mt-2 flex gap-4 text-xs opacity-70">
              <span>CTR: {stats.ctr_percent}%</span>
              <span>CR: {stats.cr_percent}%</span>
              <span>RPS: {stats.estimated_revenue_per_send} zł/wysłanie</span>
            </div>
          </div>
        )}

        <div className="space-y-4 mt-4">

          {/* Briefing */}
          <div>
            <Label>Briefing dla agenta</Label>
            <textarea
              value={extras.agent_briefing || ''}
              onChange={(e) => setExtras(prev => ({ ...prev, agent_briefing: e.target.value }))}
              placeholder="Dla kogo ta oferta, kiedy proponować, jakim tonem (np. 'doradca-kumpel', 'profesjonalnie'), czego unikać..."
              className="w-full min-h-24 p-2 mt-1 rounded border border-border bg-background text-sm"
              maxLength={5000}
            />
          </div>

          {/* Target segments */}
          <div>
            <Label>Segmenty docelowe</Label>
            <div className="flex gap-2 mt-1">
              {SEGMENTS.map(s => (
                <Badge key={s} variant={extras.target_segments?.includes(s) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleArr('target_segments', s)}>{s}</Badge>
              ))}
            </div>
          </div>

          {/* Channels */}
          <div>
            <Label>Kanały</Label>
            <div className="flex gap-2 mt-1">
              {CHANNELS.map(c => (
                <Badge key={c} variant={extras.channels?.includes(c) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleArr('channels', c)}>{c}</Badge>
              ))}
            </div>
          </div>

          {/* Conversion event + payout + caps */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Event konwersji</Label>
              <select value={extras.conversion_event} onChange={(e) => setExtras(prev => ({ ...prev, conversion_event: e.target.value }))} className="w-full mt-1 p-2 rounded border border-border bg-background text-sm">
                {CONV_EVENTS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Wypłata za konwersję (zł)</Label>
              <Input type="number" step="0.01" value={extras.payout_per_conversion} onChange={(e) => setExtras(prev => ({ ...prev, payout_per_conversion: parseFloat(e.target.value) || 0 }))} className="mt-1" />
            </div>
            <div>
              <Label>Dzienny limit wysyłek (0 = bez limitu)</Label>
              <Input type="number" value={extras.daily_send_cap} onChange={(e) => setExtras(prev => ({ ...prev, daily_send_cap: parseInt(e.target.value) || 0 }))} className="mt-1" />
            </div>
            <div>
              <Label>Cooldown per kontakt (dni)</Label>
              <Input type="number" value={extras.per_contact_cooldown_days} onChange={(e) => setExtras(prev => ({ ...prev, per_contact_cooldown_days: parseInt(e.target.value) || 0 }))} className="mt-1" />
            </div>
          </div>

          {/* Validity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ważność od (opcjonalnie)</Label>
              <Input type="date" value={(extras.validity_from || '').slice(0, 10)} onChange={(e) => setExtras(prev => ({ ...prev, validity_from: e.target.value || null }))} className="mt-1" />
            </div>
            <div>
              <Label>Ważność do (opcjonalnie)</Label>
              <Input type="date" value={(extras.validity_to || '').slice(0, 10)} onChange={(e) => setExtras(prev => ({ ...prev, validity_to: e.target.value || null }))} className="mt-1" />
            </div>
          </div>

          {/* Epidemos category */}
          <div>
            <Label>Kategoria epidemos (do auto-fetch artykułów wsparcia, Sprint 3)</Label>
            <Input value={extras.epidemos_category || ''} onChange={(e) => setExtras(prev => ({ ...prev, epidemos_category: e.target.value || null }))} placeholder="np. pozyczki, karty_kredytowe, ubezpieczenia" className="mt-1" />
          </div>

          {/* Supporting articles */}
          <div>
            <div className="flex items-center justify-between">
              <Label>Artykuły wsparcia (URL)</Label>
              <Button
                variant="outline" size="sm"
                onClick={async () => {
                  try {
                    const r = await api.post(`/offers/${offer.id}/supporting-from-epidemos?limit=3`);
                    if (r.data.ok) {
                      toast.success(`Pobrano ${r.data.fetched} artykułów z epidemos`);
                      // odśwież extras
                      const fresh = await api.get(`/offers/${offer.id}`);
                      setExtras({ ...DEFAULT_EXTRAS, ...(fresh.data.extras || {}) });
                    } else {
                      toast.warning(r.data.reason || 'Brak artykułów');
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Błąd pobierania');
                  }
                }}
                title={`Pobierze top 3 artykuły z epidemos po kategorii "${extras.epidemos_category || extras.category || '(brak)'}"`}
              >
                Pobierz z epidemos
              </Button>
            </div>
            <div className="space-y-1 mt-1">
              {(extras.supporting_articles || []).map((url, i) => (
                <div key={i} className="flex items-center gap-2 text-xs p-2 rounded bg-muted/30">
                  <a href={url} target="_blank" rel="noreferrer" className="flex-1 truncate text-primary">{url}</a>
                  <Button variant="ghost" size="sm" className="h-6" onClick={() => removeArticle(url)}>×</Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={newArticle} onChange={(e) => setNewArticle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addArticle()} placeholder="https://torr.pl/blog/ranking-pozyczek" />
                <Button variant="outline" size="sm" onClick={addArticle}>+</Button>
              </div>
            </div>
          </div>

          {/* Copy examples */}
          <div>
            <Label>Przykłady copy (treningowe dla agenta)</Label>
            <div className="space-y-2 mt-1">
              {(extras.copy_examples || []).map((c, i) => (
                <div key={i} className="p-2 rounded bg-muted/30 text-xs">
                  <div className="flex justify-between items-start mb-1">
                    <Badge variant="outline" className="text-[10px]">{c.channel}</Badge>
                    <Button variant="ghost" size="sm" className="h-5" onClick={() => removeCopy(i)}>×</Button>
                  </div>
                  {c.subject && <div className="font-medium">{c.subject}</div>}
                  <div className="whitespace-pre-wrap opacity-90">{c.body}</div>
                  {c.note && <div className="opacity-60 mt-1 italic">— {c.note}</div>}
                </div>
              ))}
              <div className="border border-border/30 rounded p-2 space-y-1">
                <div className="flex gap-2">
                  <select value={newCopy.channel} onChange={(e) => setNewCopy(prev => ({ ...prev, channel: e.target.value }))} className="p-1 rounded border border-border bg-background text-xs">
                    {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {newCopy.channel === 'email' && (
                    <Input value={newCopy.subject} onChange={(e) => setNewCopy(prev => ({ ...prev, subject: e.target.value }))} placeholder="temat email" className="text-xs h-7" />
                  )}
                </div>
                <textarea value={newCopy.body} onChange={(e) => setNewCopy(prev => ({ ...prev, body: e.target.value }))} placeholder="treść" className="w-full min-h-16 p-2 rounded border border-border bg-background text-xs" />
                <div className="flex gap-2">
                  <Input value={newCopy.note} onChange={(e) => setNewCopy(prev => ({ ...prev, note: e.target.value }))} placeholder="notatka (np. 'wariant żartobliwy')" className="text-xs h-7 flex-1" />
                  <Button variant="outline" size="sm" onClick={addCopy}>+ dodaj</Button>
                </div>
              </div>
            </div>
          </div>

          {/* Notes (admin) */}
          <div>
            <Label>Notatki wewnętrzne</Label>
            <textarea value={extras.notes || ''} onChange={(e) => setExtras(prev => ({ ...prev, notes: e.target.value }))} placeholder="Notatki dla zespołu (nie używane przez agenta)" className="w-full min-h-16 p-2 mt-1 rounded border border-border bg-background text-xs" maxLength={5000} />
          </div>

          <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-background/95 backdrop-blur py-3 border-t border-border/30">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Anuluj</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Zapisywanie…' : 'Zapisz briefing'}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
